// Serviço para gerenciar empresas no Supabase
import { supabase } from '../lib/supabase';
import type { Empresa, Endereco } from '../types';

// Interface para empresa no banco de dados
interface EmpresaDB {
  id: string;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  inscricao_estadual: string;
  email: string;
  telefone: string;
  endereco_id: string;
  conta_id: string;
  logo?: string;
  created_at: string;
  updated_at: string;
}

// Converter empresa do banco para o formato da aplicação
function empresaDBToEmpresa(empresaDB: any, endereco: Endereco): Empresa {
  return {
    id: empresaDB.id,
    razaoSocial: empresaDB.razao_social,
    nomeFantasia: empresaDB.nome_fantasia,
    cnpj: empresaDB.cnpj,
    inscricaoEstadual: empresaDB.inscricao_estadual,
    email: empresaDB.email,
    telefone: empresaDB.telefone,
    endereco,
    // Logo pode não existir na tabela, então tratamos como opcional
    logo: empresaDB.logo || undefined,
  };
}

// Buscar todas as empresas do usuário autenticado
// No contexto Admin, busca TODAS as empresas
export async function getEmpresas(): Promise<Empresa[]> {
  try {
    // Selecionar apenas as colunas essenciais para evitar recursão nas políticas RLS
    // Não usar joins com enderecos para evitar recursão
    // Usar (supabase as any) para evitar problemas de tipagem com RLS
    const { data: empresas, error } = await (supabase as any)
      .from('empresas')
      .select('id, razao_social, nome_fantasia, cnpj, inscricao_estadual, email, telefone, endereco_id, conta_id')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[empresasService] Erro ao buscar empresas:', error);
      throw error;
    }

    if (!empresas || empresas.length === 0) {
      return [];
    }

    // Buscar endereços separadamente para cada empresa para evitar recursão
    const empresasComEnderecos = await Promise.all(
      empresas.map(async (empresa: any) => {
        let endereco: Endereco = {
          cep: '',
          logradouro: '',
          numero: '',
          bairro: '',
          cidade: '',
          estado: '',
        };

        if (empresa.endereco_id) {
          try {
            const { data: enderecoData, error: enderecoError } = await supabase
              .from('enderecos')
              .select('cep, logradouro, numero, complemento, bairro, cidade, estado')
              .eq('id', empresa.endereco_id)
              .single();
            
            if (!enderecoError && enderecoData) {
              endereco = {
                cep: enderecoData.cep || '',
                logradouro: enderecoData.logradouro || '',
                numero: enderecoData.numero || '',
                complemento: enderecoData.complemento || undefined,
                bairro: enderecoData.bairro || '',
                cidade: enderecoData.cidade || '',
                estado: enderecoData.estado || '',
              };
            }
          } catch (err) {
            console.warn('[empresasService] Erro ao buscar endereço para empresa:', empresa.id, err);
            // Continuar com endereço vazio
          }
        }

        return empresaDBToEmpresa(empresa, endereco);
      })
    );

    return empresasComEnderecos;
  } catch (error) {
    console.error('[empresasService] Erro ao buscar empresas:', error);
    throw error;
  }
}

// Buscar empresa por ID
export async function getEmpresaById(id: string): Promise<Empresa | null> {
  try {
    // Selecionar apenas as colunas essenciais para evitar recursão nas políticas RLS
    // Não usar select(*) ou joins complexos que podem causar recursão
    // Primeiro, buscar empresa sem endereco
    const { data: empresa, error } = await supabase
      .from('empresas')
      .select('id, razao_social, nome_fantasia, cnpj, inscricao_estadual, email, telefone, endereco_id, conta_id')
      .eq('id', id)
      .single();

    if (error) {
      console.error('[empresasService] Erro ao buscar empresa:', error);
      // Se falhar, tentar buscar sem endereco (pode ser que a política RLS permita)
      const { data: empresaSimples, error: errorSimples } = await supabase
        .from('empresas')
        .select('id, razao_social, nome_fantasia, cnpj, inscricao_estadual, email, telefone, endereco_id, conta_id')
        .eq('id', id)
        .single();
      
      if (errorSimples) {
        console.error('[empresasService] Erro ao buscar empresa (simples):', errorSimples);
        throw errorSimples;
      }
      
      if (!empresaSimples) return null;
      
      // Se não conseguiu buscar endereço, criar um endereço vazio
      const endereco: Endereco = {
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
      };

      return empresaDBToEmpresa(empresaSimples, endereco);
    }
    
    if (!empresa) return null;

    // Buscar endereço separadamente para evitar recursão nas políticas RLS
    let endereco: Endereco = {
      cep: '',
      logradouro: '',
      numero: '',
      bairro: '',
      cidade: '',
      estado: '',
    };

    if (empresa.endereco_id) {
      try {
        const { data: enderecoData, error: enderecoError } = await supabase
          .from('enderecos')
          .select('cep, logradouro, numero, complemento, bairro, cidade, estado')
          .eq('id', empresa.endereco_id)
          .single();
        
        if (!enderecoError && enderecoData) {
          endereco = {
            cep: enderecoData.cep || '',
            logradouro: enderecoData.logradouro || '',
            numero: enderecoData.numero || '',
            complemento: enderecoData.complemento || undefined,
            bairro: enderecoData.bairro || '',
            cidade: enderecoData.cidade || '',
            estado: enderecoData.estado || '',
          };
        }
      } catch (err) {
        console.warn('[empresasService] Erro ao buscar endereço:', err);
        // Continuar com endereço vazio
      }
    }

    return empresaDBToEmpresa(empresa, endereco);
  } catch (error) {
    console.error('Erro ao buscar empresa:', error);
    throw error;
  }
}

// Criar nova empresa
export async function createEmpresa(empresa: Omit<Empresa, 'id'>, contaId: string): Promise<Empresa> {
  try {
    // Primeiro, criar o endereço
    const { data: endereco, error: enderecoError } = await supabase
      .from('enderecos')
      .insert({
        cep: empresa.endereco.cep,
        logradouro: empresa.endereco.logradouro,
        numero: empresa.endereco.numero,
        complemento: empresa.endereco.complemento || null,
        bairro: empresa.endereco.bairro,
        cidade: empresa.endereco.cidade,
        estado: empresa.endereco.estado,
      })
      .select()
      .single();

    if (enderecoError) throw enderecoError;

    // Depois, criar a empresa (sem logo, pois a coluna pode não existir)
    const empresaData: any = {
      razao_social: empresa.razaoSocial,
      nome_fantasia: empresa.nomeFantasia,
      cnpj: empresa.cnpj,
      inscricao_estadual: empresa.inscricaoEstadual,
      email: empresa.email,
      telefone: empresa.telefone,
      endereco_id: endereco.id,
      conta_id: contaId,
    };
    
    // Adicionar logo apenas se estiver definido (e a coluna existir)
    if (empresa.logo) {
      empresaData.logo = empresa.logo;
    }
    
    const { data: novaEmpresa, error: empresaError } = await supabase
      .from('empresas')
      .insert(empresaData)
      .select()
      .single();

    if (empresaError) throw empresaError;

    return empresaDBToEmpresa(novaEmpresa, empresa.endereco);
  } catch (error) {
    console.error('Erro ao criar empresa:', error);
    throw error;
  }
}

// Atualizar empresa
export async function updateEmpresa(id: string, empresa: Partial<Empresa>): Promise<Empresa> {
  try {
    // Buscar empresa atual para obter endereco_id
    const { data: empresaAtual, error: buscaError } = await supabase
      .from('empresas')
      .select('endereco_id, enderecos(*)')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Atualizar endereço se fornecido
    if (empresa.endereco && empresaAtual.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .update({
          cep: empresa.endereco.cep,
          logradouro: empresa.endereco.logradouro,
          numero: empresa.endereco.numero,
          complemento: empresa.endereco.complemento || null,
          bairro: empresa.endereco.bairro,
          cidade: empresa.endereco.cidade,
          estado: empresa.endereco.estado,
        })
        .eq('id', empresaAtual.endereco_id);

      if (enderecoError) throw enderecoError;
    }

    // Atualizar empresa
    const updateData: any = {};
    if (empresa.razaoSocial !== undefined) updateData.razao_social = empresa.razaoSocial;
    if (empresa.nomeFantasia !== undefined) updateData.nome_fantasia = empresa.nomeFantasia;
    if (empresa.cnpj !== undefined) updateData.cnpj = empresa.cnpj;
    if (empresa.inscricaoEstadual !== undefined) updateData.inscricao_estadual = empresa.inscricaoEstadual;
    if (empresa.email !== undefined) updateData.email = empresa.email;
    if (empresa.telefone !== undefined) updateData.telefone = empresa.telefone;
    // Logo pode não existir na tabela, então só atualizamos se estiver definido
    // Não incluímos logo se não estiver definido para evitar erro de coluna não encontrada

    const { data: empresaAtualizada, error: empresaError } = await supabase
      .from('empresas')
      .update(updateData)
      .eq('id', id)
      .select('*, enderecos(*)')
      .single();

    if (empresaError) throw empresaError;

    const endereco: Endereco = {
      cep: empresaAtualizada.enderecos?.cep || '',
      logradouro: empresaAtualizada.enderecos?.logradouro || '',
      numero: empresaAtualizada.enderecos?.numero || '',
      complemento: empresaAtualizada.enderecos?.complemento || undefined,
      bairro: empresaAtualizada.enderecos?.bairro || '',
      cidade: empresaAtualizada.enderecos?.cidade || '',
      estado: empresaAtualizada.enderecos?.estado || '',
    };

    return empresaDBToEmpresa(empresaAtualizada, endereco);
  } catch (error) {
    console.error('Erro ao atualizar empresa:', error);
    throw error;
  }
}

// Deletar empresa
export async function deleteEmpresa(id: string): Promise<void> {
  try {
    // Buscar empresa para obter endereco_id
    const { data: empresa, error: buscaError } = await supabase
      .from('empresas')
      .select('endereco_id')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Deletar empresa
    const { error: empresaError } = await supabase
      .from('empresas')
      .delete()
      .eq('id', id);

    if (empresaError) throw empresaError;

    // Deletar endereço se não estiver sendo usado
    if (empresa.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .delete()
        .eq('id', empresa.endereco_id);

      // Não lançar erro se o endereço estiver sendo usado por outros registros
      if (enderecoError && !enderecoError.message.includes('violates foreign key')) {
        console.warn('Erro ao deletar endereço:', enderecoError);
      }
    }
  } catch (error) {
    console.error('Erro ao deletar empresa:', error);
    throw error;
  }
}

